<template>
  <div class="wrapper">
    <div class="actions-container">
      <AnswerFilters
        :statuses_filter_control="selectedStatuses"
        :toggle_status_filter="toggleStatusFilter"
        :on_date_change="updateDateRange"
      />

      <ExportButton
        :survey_status="survey_status"
        :toggle_loading="toggleLoading"
        :disabled="
          loadBlocked ? false : filteredAnswers.data && !filteredAnswers.data.length
        "
        :active_filters="{ dateRange, statuses: statusFilterList }"
      />
    </div>

    <div v-if="errorMessage" class="message-container">
      <p>{{ errorMessage }}</p>
    </div>

    <Loader v-if="loading" />

    <AnswersTable v-else :answers="filteredAnswers" :toggle_loading="toggleLoading" />
  </div>
</template>

<script>
import { endOfDay, startOfDay } from 'date-fns'

import Loader from '@/components/loading/Loader'

import AnswerFilters, { selectedStatusBase } from './AnswerFilters.vue'
import ExportButton from './ExportButton.vue'
import AnswersTable from './AnswersTable.vue'

import reportingToolAPI from '@/services/reportingTool'

import * as translationIndex from './index.translations'

const { default: translations, ...translationKeys } = translationIndex

const SLOW_LOAD_THRESHOLD = 2000
const NO_LOAD_THRESHOLD = 4500

export default {
  name: 'SurveyDatabase',

  components: {
    AnswerFilters,
    ExportButton,
    Loader,
    AnswersTable,
  },

  data() {
    return {
      loading: false,
      errorMessage: '',
      answers: {
        columns: [],
        data: [],
      },
      translations,
      selectedStatuses: selectedStatusBase,
      dateRange: [],
      loadBlocked: false,
      ...translationKeys,
    }
  },

  props: {
    survey_status: {
      required: true,
      type: Object,
    },
  },

  computed: {
    statusFilterList() {
      return Object.keys(this.selectedStatuses).filter(
        status => this.selectedStatuses[status],
      )
    },

    filteredAnswers() {
      // this is due to old report api return. Will be modified in the future
      const START_DATE_INDEX = 4
      const STATUS_INDEX = 2

      const [hasStatusFilter, hasDateFilter] = [
        this.statusFilterList,
        this.dateRange,
      ].map(list => !!list.length)

      if (!hasStatusFilter && !hasDateFilter) return this.answers

      return {
        ...this.answers,
        data:
          this.answers.data?.filter(answerRow => {
            const [status, startDate] = [STATUS_INDEX, START_DATE_INDEX].map(
              index => answerRow[index],
            )

            const statusOk = hasStatusFilter
              ? this.statusFilterList.includes(status)
              : true

            const dateOk = hasDateFilter ? this.determineIfDateInBetween(startDate) : true

            return dateOk && statusOk
          }) || [],
      }
    },
  },

  methods: {
    toggleLoading() {
      this.loading = !this.loading
    },

    toggleStatusFilter(status) {
      this.selectedStatuses = {
        ...this.selectedStatuses,
        [status]: !this.selectedStatuses[status],
      }
    },

    updateDateRange(newRange) {
      this.dateRange = newRange.filter(Boolean)
    },

    determineIfDateInBetween(dateString) {
      const [startDate, endDate] = this.dateRange

      const date = new Date(dateString)

      return startOfDay(startDate) <= date && date <= endOfDay(endDate)
    },

    async getAllAnswers() {
      this.toggleLoading()

      const surveyId = this.survey_status.survey_id

      try {
        const { data } = await reportingToolAPI.get(`/survey/data?surveyId=${surveyId}`)

        this.answers = data

        this.errorMessage = ''
      } catch (err) {
        this.errorMessage = this.parseTranslation(this.ERROR_MESSAGE_LOAD_ANSWER)
      } finally {
        this.toggleLoading()
      }
    },
  },

  created() {
    const { starts } = this.survey_status

    this.loadBlocked = starts >= NO_LOAD_THRESHOLD

    if (starts > SLOW_LOAD_THRESHOLD) {
      const messageKey = this.loadBlocked ? this.LOAD_BLOCKED : this.TAKE_A_WHILE_TO_LOAD

      this.errorMessage = this.parseTranslation(messageKey, starts)
    }

    if (this.loadBlocked) return

    this.getAllAnswers()
  },
}
</script>

<style lang="scss" scoped>
.actions-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.message-container {
  margin: 36px auto;

  p {
    text-align: center;
    color: $gray;
  }
}
</style>
